import { useState, useRef, useCallback } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Search, Code2, ShieldCheck } from 'lucide-react';

const steps = [
  {
    num: '01',
    title: 'Discover',
    icon: Search,
    desc: 'We start by understanding your business, not just your technology. Strategy, architecture, and a clear roadmap before a single line of code.',
    tags: ['Stakeholder interviews', 'Tech audit', 'Architecture blueprint'],
    color: '#F97316',
    glowColor: 'rgba(249,115,22,0.15)',
  },
  {
    num: '02',
    title: 'Build',
    icon: Code2,
    desc: 'Senior engineers and architects deliver working software in agile sprints. You see progress every week, not every quarter.',
    tags: ['Agile sprints', 'CI/CD pipeline', 'Weekly demos'],
    color: '#8B5CF6',
    glowColor: 'rgba(139,92,246,0.15)',
  },
  {
    num: '03',
    title: 'Protect & Scale',
    icon: ShieldCheck,
    desc: 'Security is built in from line one. After launch, we monitor, optimize, and grow the system alongside your business.',
    tags: ['Pen testing', '24/7 monitoring', 'Performance optimization'],
    color: '#06B6D4',
    glowColor: 'rgba(6,182,212,0.15)',
  },
];

function StepCard({ step, index, isActive, onClick, mouse }) {
  const Icon = step.icon;
  const parallaxX = mouse.x * (index === 0 ? -8 : index === 2 ? 8 : 0);
  const parallaxY = mouse.y * (index === 0 ? -5 : index === 2 ? 5 : -3);

  return (
    <motion.div
      data-testid={`process-step-${index}`}
      className="relative cursor-pointer group"
      style={{ transformStyle: 'preserve-3d' }}
      animate={{
        z: isActive ? 60 : 0,
        scale: isActive ? 1.05 : 0.95,
        x: parallaxX,
        y: parallaxY,
      }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      whileHover={{ scale: isActive ? 1.07 : 1.0, z: isActive ? 70 : 20 }}
    >
      {/* Glow behind card */}
      <div
        className="absolute -inset-3 rounded-3xl blur-2xl transition-opacity duration-700"
        style={{
          background: step.glowColor,
          opacity: isActive ? 1 : 0,
        }}
      />

      {/* Card */}
      <div
        className="relative rounded-2xl overflow-hidden transition-all duration-500"
        style={{
          background: isActive
            ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
          border: isActive ? `1px solid ${step.color}40` : '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(16px)',
          boxShadow: isActive
            ? `0 24px 60px ${step.glowColor}, 0 0 1px ${step.color}60`
            : '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* Top accent bar */}
        <motion.div
          className="h-[2px] w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${step.color}, transparent)` }}
          animate={{ opacity: isActive ? 1 : 0.2 }}
          transition={{ duration: 0.5 }}
        />

        <div className="p-7 lg:p-8">
          {/* Number + Icon row */}
          <div className="flex items-center justify-between mb-6">
            <motion.span
              className="font-black text-5xl tracking-tight"
              style={{ color: isActive ? step.color : 'rgba(255,255,255,0.1)' }}
              animate={{ scale: isActive ? 1 : 0.9 }}
            >
              {step.num}
            </motion.span>
            <motion.div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: isActive ? `${step.color}18` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${isActive ? `${step.color}35` : 'rgba(255,255,255,0.06)'}`,
              }}
              animate={{ rotate: isActive ? 0 : -5 }}
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              <Icon
                size={22}
                style={{ color: isActive ? step.color : 'rgba(255,255,255,0.25)' }}
                strokeWidth={1.5}
              />
            </motion.div>
          </div>

          {/* Title */}
          <h3
            className="font-bold text-xl mb-3 transition-colors duration-500"
            style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.35)' }}
          >
            {step.title}
          </h3>

          {/* Description */}
          <p
            className="text-sm leading-relaxed mb-5 transition-colors duration-500"
            style={{ color: isActive ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)' }}
          >
            {step.desc}
          </p>

          {/* Tags */}
          <motion.div
            className="flex flex-wrap gap-2"
            animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 8 }}
            transition={{ duration: 0.4, delay: isActive ? 0.2 : 0 }}
          >
            {step.tags.map((tag) => (
              <span
                key={tag}
                className="text-[11px] font-medium px-3 py-1 rounded-full"
                style={{
                  color: step.color,
                  background: `${step.color}12`,
                  border: `1px solid ${step.color}20`,
                }}
              >
                {tag}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Corner accent */}
        <div
          className="absolute bottom-3 right-3 w-6 h-6 border-r border-b rounded-br transition-colors duration-500"
          style={{ borderColor: isActive ? `${step.color}30` : 'rgba(255,255,255,0.04)' }}
        />
      </div>
    </motion.div>
  );
}

export default function HowWeWork() {
  const [active, setActive] = useState(1);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setMouse({ x, y });
  }, []);

  const goTo = useCallback((idx) => setActive(idx), []);

  return (
    <section
      ref={sectionRef}
      data-testid="how-we-work-section"
      className="relative py-20 lg:py-28 overflow-hidden"
      onMouseMove={handleMouseMove}
      style={{ background: 'linear-gradient(180deg, #06080F 0%, #0a0e1a 50%, #06080F 100%)' }}
    >
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Ambient orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[120px]"
          style={{ background: 'rgba(249,115,22,0.04)', y: bgY }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-[100px]"
          style={{ background: 'rgba(139,92,246,0.05)', y: useTransform(scrollYProgress, [0, 1], [-30, 30]) }}
        />
        <motion.div
          className="absolute top-1/2 right-1/3 w-72 h-72 rounded-full blur-[90px]"
          style={{ background: 'rgba(6,182,212,0.04)', y: useTransform(scrollYProgress, [0, 1], [20, -20]) }}
        />
      </div>

      <div className="relative z-10 max-w-content mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
        >
          <div>
            <p className="text-orange-500 font-semibold text-xs tracking-[0.25em] uppercase mb-3">OUR PROCESS</p>
            <h2 data-testid="how-we-work-heading" className="font-black tracking-tight text-white" style={{ fontSize: 'clamp(1.75rem, 1.25rem + 2vw, 2.75rem)', lineHeight: 1.1 }}>
              Three Steps. One Partner.<br />
              <span className="bg-gradient-to-r from-[#F97316] via-[#8B5CF6] to-[#06B6D4] bg-clip-text text-transparent">Zero Gaps.</span>
            </h2>
          </div>
          <a
            href="/assessment"
            data-testid="how-we-work-cta"
            className="group inline-flex items-center gap-2 text-orange-500 text-sm font-medium mt-4 lg:mt-0 flex-shrink-0 hover:text-orange-400 transition-colors"
          >
            Start With a Conversation <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </motion.div>

        {/* 3D Cards area */}
        <div style={{ perspective: '1200px' }}>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7"
            style={{
              transformStyle: 'preserve-3d',
              rotateY: mouse.x * 2,
              rotateX: mouse.y * -1.5,
            }}
          >
            {steps.map((step, i) => (
              <StepCard
                key={step.num}
                step={step}
                index={i}
                isActive={i === active}
                onClick={() => goTo(i)}
                mouse={mouse}
              />
            ))}
          </motion.div>
        </div>

        {/* Connection line + dots */}
        <div className="hidden md:flex items-center justify-center gap-0 mt-10">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center">
              <motion.button
                onClick={() => goTo(i)}
                className="relative w-4 h-4 rounded-full flex items-center justify-center"
                whileHover={{ scale: 1.3 }}
                aria-label={`Go to step ${i + 1}`}
              >
                <motion.div
                  className="w-3 h-3 rounded-full"
                  animate={{
                    background: i === active ? step.color : 'rgba(255,255,255,0.15)',
                    boxShadow: i === active ? `0 0 12px ${step.color}60` : '0 0 0 transparent',
                  }}
                  transition={{ duration: 0.4 }}
                />
                {i === active && (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ border: `1px solid ${step.color}40` }}
                    animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}
              </motion.button>
              {i < steps.length - 1 && (
                <div className="relative w-32 lg:w-48 h-px mx-2">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/10" />
                  <motion.div
                    className="absolute top-0 h-px"
                    style={{
                      background: i < active
                        ? `linear-gradient(90deg, ${steps[i].color}, ${steps[i + 1].color})`
                        : 'transparent',
                      width: i < active ? '100%' : '0%',
                    }}
                    animate={{ width: i < active ? '100%' : '0%' }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                  {/* Traveling light pulse */}
                  {i < active && (
                    <motion.div
                      className="absolute top-[-1px] w-8 h-[3px] rounded-full"
                      style={{ background: `linear-gradient(90deg, transparent, ${steps[i + 1].color}, transparent)` }}
                      animate={{ left: ['-10%', '110%'] }}
                      transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile dots */}
        <div className="flex md:hidden items-center justify-center gap-3 mt-8">
          {steps.map((step, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === active ? 32 : 8,
                height: 8,
                background: i === active ? step.color : 'rgba(255,255,255,0.15)',
                boxShadow: i === active ? `0 0 10px ${step.color}40` : 'none',
              }}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
